/** Normalized combat facts. This module owns mitigation and HP/shield application. */
import type { MechanicalDamageType } from '../types.js';

/** Every damage class, in the one order every per-class map is built in. */
export const DAMAGE_CLASSES = Object.freeze(['PHYSICAL', 'MAGICAL', 'NONE'] as const);
export type DamageClass = typeof DAMAGE_CLASSES[number];
/** Every attribute, in the one order every per-attribute map is built in. */
export const ATTRIBUTES = Object.freeze(['FIRE', 'COLD', 'LIGHTNING', 'TOXIC', 'ARCANE', 'NONE'] as const);
export type Attribute = typeof ATTRIBUTES[number];
export type MitigationMode = 'NORMAL' | 'TRUE';
export type DamageSourceKind = 'DIRECT' | 'PERIODIC' | 'SECONDARY' | 'ENVIRONMENT';

export interface DamageComponent {
  amount: number;
  damageClass: DamageClass;
  attribute: Attribute;
  mitigationMode: MitigationMode;
}

export function damageComponent(
  amount: number,
  type: MechanicalDamageType = 'physical',
  trueDamage = false,
): DamageComponent {
  if (trueDamage) return { amount, damageClass: 'NONE', attribute: 'NONE', mitigationMode: 'TRUE' };
  const map: Record<MechanicalDamageType, [DamageClass, Attribute, MitigationMode]> = {
    physical: ['PHYSICAL', 'NONE', 'NORMAL'],
    fire: ['MAGICAL', 'FIRE', 'NORMAL'],
    cold: ['MAGICAL', 'COLD', 'NORMAL'],
    lightning: ['MAGICAL', 'LIGHTNING', 'NORMAL'],
    toxic: ['MAGICAL', 'TOXIC', 'NORMAL'],
    arcane: ['MAGICAL', 'ARCANE', 'NORMAL'],
  };
  const [damageClass, attribute, mitigationMode] = map[type];
  return { amount, damageClass, attribute, mitigationMode };
}

/** Reverse of `damageComponent()`'s forward map, for deterministic telemetry
 * attribution (DT.1 §D6). Returns `undefined` for true damage — it has no
 * mechanical type identity, and is never force-mapped to one. */
export function mechanicalDamageTypeOf(component: DamageComponent): MechanicalDamageType | undefined {
  if (component.mitigationMode === 'TRUE') return undefined;
  if (component.damageClass === 'PHYSICAL') return 'physical';
  switch (component.attribute) {
    case 'FIRE': return 'fire';
    case 'COLD': return 'cold';
    case 'LIGHTNING': return 'lightning';
    case 'TOXIC': return 'toxic';
    case 'ARCANE': return 'arcane';
    default: return undefined;
  }
}

export interface CombatDefenses {
  physicalDefense?: number;
  magicDefense?: number;
  /** Decimal fractions; negative values deliberately represent weakness. */
  attributeResistance?: Partial<Record<Attribute, number>>;
  damageTakenMultiplier?: number;
}

export interface CombatShield {
  readonly instanceId: number;
  remaining: number;
  readonly createdTick: number;
  readonly expireTick: number | null;
  rootContext?: import('./root-context.js').RootContextHandle;
}

export interface CombatBalance {
  defenseK: number;
  minResistance: number;
  maxResistance: number;
  allowNegativeDefense: boolean;
  roundAppliedDamage: (amount: number) => number;
}

export const DEFAULT_COMBAT_BALANCE: CombatBalance = {
  defenseK: 100,
  minResistance: -1,
  maxResistance: 0.9,
  allowNegativeDefense: false,
  // Bootstrap preserves the simulator's continuous HP scale; integer games inject Math.round here.
  roundAppliedDamage: (amount) => amount,
};

export interface CombatTarget {
  id: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  shields: CombatShield[];
  combatDefenses?: CombatDefenses;
}

export interface ShieldCreationContext {
  readonly currentTick: number;
  readonly tickRate: number;
  allocateShieldInstanceId(): number;
}

export interface CreateShieldInput {
  readonly remaining: number;
  readonly durationS?: number | null;
  readonly expireTick?: number | null;
  readonly rootContext?: import('./root-context.js').RootContextHandle;
}

function validTick(value: number): boolean { return Number.isSafeInteger(value) && value >= 0; }
function validId(value: number): boolean { return Number.isSafeInteger(value) && value > 0; }

/** Creates the canonical shield shape using a caller-owned allocator. */
export function createShield(input: CreateShieldInput, context: ShieldCreationContext): CombatShield | null {
  if (!(Number.isFinite(context.tickRate) && context.tickRate > 0) || !validTick(context.currentTick)) throw new Error('invalid shield creation context');
  if (!(Number.isFinite(input.remaining) && input.remaining >= 0)) throw new Error('invalid shield amount');
  const instanceId = context.allocateShieldInstanceId();
  if (!validId(instanceId)) throw new Error('shield instance IDs must be positive safe integers');
  let expireTick: number | null;
  if (input.expireTick !== undefined) expireTick = input.expireTick;
  else if (input.durationS === null || input.durationS === undefined) expireTick = null;
  else {
    if (!(Number.isFinite(input.durationS) && input.durationS >= 0)) throw new Error('invalid shield duration');
    if (input.durationS === 0) return null;
    expireTick = context.currentTick + Math.max(1, Math.ceil(input.durationS * context.tickRate));
  }
  if (expireTick !== null && !validTick(expireTick)) throw new Error('invalid shield expiry tick');
  return { instanceId, remaining: Math.max(0, input.remaining), createdTick: context.currentTick, expireTick,
    rootContext: input.rootContext };
}

export interface DamageEvent {
  targetId: number;
  sourceKind: DamageSourceKind;
  components: readonly DamageComponent[];
  atS: number;
  /**
   * Tick at which the event resolves. When present it is the sole clock for
   * excluding tick-expired shields; when absent (isolated fixtures with no
   * tick allocator) shield expiry is left entirely to the actor lifecycle.
   */
  atTick?: number;
  executionId?: number;
  root?: { index: number; generation: number };
  attackId?: number;
  /** The composite stage of the attack that caused this damage. */
  stageId?: string;
}

export interface ComponentDamageResult {
  component: DamageComponent;
  preMitigationAmount: number;
  postMitigationAmount: number;
  shieldAbsorbed: number;
  hpDamage: number;
  overkillAmount: number;
}

export interface DamageResult {
  event: DamageEvent;
  preMitigationAmount: number;
  incomingAmount: number;
  shieldAbsorbed: number;
  hpDamage: number;
  overkillAmount: number;
  componentBreakdown: ComponentDamageResult[];
}

export interface DeathEvent { targetId: number; atS: number; sourceKind: DamageSourceKind; executionId?: number; root?: { index: number; generation: number }; attackId?: number; }

function finiteNonNegative(value: number): number { return Number.isFinite(value) ? Math.max(0, value) : 0; }

export function normalizeDamageComponents(components: readonly DamageComponent[]): DamageComponent[] {
  const merged = new Map<string, DamageComponent>();
  for (const component of components) {
    const key = `${component.damageClass}|${component.attribute}|${component.mitigationMode}`;
    const prior = merged.get(key);
    if (prior) prior.amount += component.amount;
    else merged.set(key, { ...component });
  }
  return [...merged.entries()].sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([, component]) => component);
}

function allocate(total: number, weights: readonly number[]): number[] {
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  if (weightTotal <= 0 || total <= 0) return weights.map(() => 0);
  let assigned = 0;
  return weights.map((weight, index) => {
    const value = index === weights.length - 1 ? total - assigned : total * weight / weightTotal;
    assigned += value;
    return value;
  });
}

function mitigate(component: DamageComponent, defenses: CombatDefenses | undefined, balance: CombatBalance): number {
  if (component.mitigationMode === 'TRUE') return finiteNonNegative(component.amount);
  const rawDefense = component.damageClass === 'PHYSICAL'
    ? defenses?.physicalDefense ?? 0
    : component.damageClass === 'MAGICAL' ? defenses?.magicDefense ?? 0 : 0;
  const defense = balance.allowNegativeDefense ? Math.max(-balance.defenseK + 1e-6, rawDefense) : Math.max(0, rawDefense);
  const defenseMultiplier = 1 - defense / (defense + balance.defenseK);
  const rawResistance = component.attribute === 'NONE' ? 0 : defenses?.attributeResistance?.[component.attribute] ?? 0;
  const resistance = Math.max(balance.minResistance, Math.min(balance.maxResistance, rawResistance));
  return finiteNonNegative(component.amount) * defenseMultiplier * (1 - resistance);
}

/** Applies one atomic damage event. Components are mitigated independently, then shields absorb their sum. */
export function resolveDamage(
  target: CombatTarget,
  event: DamageEvent,
  balance: CombatBalance = DEFAULT_COMBAT_BALANCE,
  options?: { readonly defenses?: CombatDefenses },
): { result: DamageResult; death: DeathEvent | null } {
  const normalizedEvent: DamageEvent = { ...event, components: normalizeDamageComponents(event.components) };
  const breakdown = normalizedEvent.components.map((component) => ({
    component,
    preMitigationAmount: finiteNonNegative(component.amount),
    postMitigationAmount: mitigate(component, options?.defenses ?? target.combatDefenses, balance),
    shieldAbsorbed: 0,
    hpDamage: 0,
    overkillAmount: 0,
  }));
  const preMitigationAmount = breakdown.reduce((sum, item) => sum + item.preMitigationAmount, 0);
  const incomingAmount = breakdown.reduce((sum, item) => sum + item.postMitigationAmount, 0)
    * ((options?.defenses ?? target.combatDefenses)?.damageTakenMultiplier ?? 1);
  // Component calculations stay in floating point; quantization happens once at the application boundary.
  let remaining = finiteNonNegative(balance.roundAppliedDamage(incomingAmount));
  let shieldAbsorbed = 0;
  // Expiry ownership cleanup belongs to the actor lifecycle before resolution.
  // The resolver merely excludes tick-expired shields from this event.
  const shieldOrder = (a: CombatShield, b: CombatShield) =>
    (a.expireTick === null ? 1 : b.expireTick === null ? -1 : a.expireTick - b.expireTick) || a.createdTick - b.createdTick || a.instanceId - b.instanceId;
  const shields = (target.shields ?? []).filter((shield) => shield.remaining > 0
    && (event.atTick === undefined || shield.expireTick === null || shield.expireTick > event.atTick)).sort(shieldOrder);
  for (const shield of shields) {
    if (remaining <= 0) break;
    const absorbed = Math.min(shield.remaining, remaining);
    shield.remaining -= absorbed;
    remaining -= absorbed;
    shieldAbsorbed += absorbed;
  }
  target.shields?.sort(shieldOrder);
  const hpDamage = Math.min(Math.max(0, target.hp), remaining);
  const overkillAmount = Math.max(0, remaining - hpDamage);
  const weights = breakdown.map((item) => item.postMitigationAmount);
  const shieldShares = allocate(shieldAbsorbed, weights);
  const hpShares = allocate(hpDamage, weights);
  const overkillShares = allocate(overkillAmount, weights);
  for (let index = 0; index < breakdown.length; index++) {
    breakdown[index]!.shieldAbsorbed = shieldShares[index] ?? 0;
    breakdown[index]!.hpDamage = hpShares[index] ?? 0;
    breakdown[index]!.overkillAmount = overkillShares[index] ?? 0;
  }
  target.hp = Math.max(0, target.hp - hpDamage);
  const died = target.alive && target.hp <= 0;
  if (died) target.alive = false;
  const result = { event: normalizedEvent, preMitigationAmount, incomingAmount, shieldAbsorbed, hpDamage, overkillAmount, componentBreakdown: breakdown };
  return { result, death: died ? { targetId: target.id, atS: event.atS, sourceKind: event.sourceKind, executionId: event.executionId, root: event.root, attackId: event.attackId } : null };
}
